import * as Sentry from '@sentry/react';
import * as I from 'immutable';
import React from 'react';

import {useAlertPoliciesForFeatureCollections} from 'app/components/Alerts/AlertPolicyProvider';
import {api} from 'app/modules/Remote';
import {AlertPolicy} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import ImageryPricesProvider from 'app/providers/ImageryPricesProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import {recordEvent} from 'app/tools/Analytics';
import * as CONSTANTS from 'app/utils/constants';
import {
  findPrimaryFeatureCollection,
  getPrimaryFeatureCollectionIdFromProjectId,
  getPrimaryFeatureCollectionIds,
} from 'app/utils/featureCollectionUtils';
import * as featureUtils from 'app/utils/featureUtils';
import {useApiGet} from 'app/utils/hookUtils';
import {LAYER_PACKAGES} from 'app/utils/layers';
import {
  checkLayerIsProcessingStatus,
  createLayerKeysFromLayerObject,
  getLayer,
  getLayerResolution,
  getPackageLayerKeys,
  parseLayerKey,
} from 'app/utils/layerUtils';
import {
  createLayerObjectFromLayerKeys,
  getEnrolledLayerKeysForFeatureCollection,
  getProcessingMetadataForFeatureCollection,
  getSupportedLayerKeysForFeatureCollection,
} from 'app/utils/layerUtils';
import {orgIsAllowedTier} from 'app/utils/organizationUtils';

import {BulkMode, PortfolioPlan} from './bulkEnroll';
import LensLibraryView from './LensLibraryView';
import {PREVIEW_LAYERS_CONFIG} from './libraryConfig';
import {LayerDataset, LibraryDataset, LibraryDatasetType} from './LibraryTypes';

export type SupportedLayersMap = Record<
  string,
  {
    supportedFeatureCollectionIds: number[];
    enrolledFeatureCollectionIds: number[];
    processingFeatureCollectionIds: number[];
  }
>;

export type LayersLibraryMode = 'librarySettings' | 'singlePortfolio';
// These layers cannot be turned on or off by users and we don't want to display them in the library
export const HIDDEN_LAYER_KEYS = [
  'UserData_high-res-truecolor',
  'COG-TRUECOLOR_high-res-truecolor',
  'WMS-TRUECOLOR_high-res-truecolor',
  'WMTS-TRUECOLOR_high-res-truecolor',
  // We've lost access to NICFI, so we don't want to display it in the library. It's been removed
  // from product configs, leaving this here in case any data sneaks through.
  'PLANET-NICFI_high-res-truecolor',
  // Layers that technically still exist but we don't want to direct customers to:
  'S2_Phycocyanin',
];
export const DEFAULT_THUMBNAIL_URL =
  'https://storage.googleapis.com/upstream-icons/layers/default.png';

export const LensLibraryProviderSettingsModeWrapper: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.MapAsRecord<I.ImmutableFields<ApiOrganization>>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
  }>
> = ({organization, profile}) => {
  const [
    projects,
    featureCollectionsById,
    {addLayersToFeatureCollections, removeLayersFromFeatureCollections},
    projectsMeta,
  ] = useProjects();
  const primaryFeatureCollectionIds = React.useMemo(
    () => getPrimaryFeatureCollectionIds(projects, featureCollectionsById),
    [projects, featureCollectionsById]
  );
  // isLoading, not isFetching: only "no policies fetched yet", so background
  // refetches don't block removals.
  const {
    data: alertPolicies,
    isLoading: alertPoliciesLoading,
    isError: alertPoliciesError,
  } = useAlertPoliciesForFeatureCollections(primaryFeatureCollectionIds);
  return (
    <LensLibraryProvider
      organization={organization}
      profile={profile}
      alertPolicies={alertPolicies || []}
      alertPoliciesLoading={alertPoliciesLoading}
      alertPoliciesError={alertPoliciesError}
      addLayersToFeatureCollections={addLayersToFeatureCollections}
      removeLayersFromFeatureCollections={removeLayersFromFeatureCollections}
      mode={'librarySettings'}
      projects={projects}
      featureCollectionsById={featureCollectionsById}
      areProjectsLoading={projectsMeta.loading}
    />
  );
};

export const LensLibraryProvider: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.MapAsRecord<I.ImmutableFields<ApiOrganization>>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    addLayersToFeatureCollections: (
      ids: number[],
      layers: Record<string, string[]>
    ) => Promise<void>;
    removeLayersFromFeatureCollections: (
      ids: number[],
      layers: Record<string, string[]>
    ) => Promise<void>;
    mode: LayersLibraryMode;
    alertPolicies: AlertPolicy[];
    alertPoliciesLoading?: boolean;
    // The lookout query failed, so `alertPolicies` says nothing about what exists.
    alertPoliciesError?: boolean;
    selectedOverlayIds?: number[];
    setSelectedOverlayIds?: React.Dispatch<React.SetStateAction<number[]>>;
    projects: I.OrderedMap<string, I.MapAsRecord<I.ImmutableFields<ApiProject>>> | null;
    featureCollectionsById: I.Map<number, I.ImmutableOf<ApiFeatureCollection>>;
    areProjectsLoading: boolean;
    onClose?: () => void;
  }>
> = ({
  organization,
  profile,
  addLayersToFeatureCollections,
  removeLayersFromFeatureCollections,
  selectedOverlayIds,
  setSelectedOverlayIds,
  mode,
  alertPolicies,
  alertPoliciesLoading = false,
  alertPoliciesError = false,
  projects,
  featureCollectionsById,
  areProjectsLoading,
  onClose,
}) => {
  const layers: LibraryDataset<LayerDataset>[] = React.useMemo(() => {
    if (!projects) {
      return [];
    }

    const featureCollections = projects.map((project) =>
      findPrimaryFeatureCollection(project!, featureCollectionsById)
    );

    const layersMap: SupportedLayersMap[] = featureCollections.size
      ? featureCollections
          ?.map((fc) => {
            const supportedLayerKeys = fc ? getSupportedLayerKeysForFeatureCollection(fc) : [];
            const enrolledLayerKeys = fc ? getEnrolledLayerKeysForFeatureCollection(fc) : [];
            const processingMetadata = fc ? getProcessingMetadataForFeatureCollection(fc) : {};

            const layersInfo: SupportedLayersMap = supportedLayerKeys.reduce(
              (layersInfo, layerKey) => {
                const isEnrolled = enrolledLayerKeys.includes(layerKey);
                const isProcessing = checkLayerIsProcessingStatus(layerKey, processingMetadata);

                const currSupportedFeatureCollectionIds =
                  layersInfo[layerKey]?.supportedFeatureCollectionIds || [];
                const currEnrolledFeatureCollectionIds =
                  layersInfo[layerKey]?.enrolledFeatureCollectionIds || [];
                const currProcessingFeatureCollectionIds =
                  layersInfo[layerKey]?.processingFeatureCollectionIds || [];

                if (HIDDEN_LAYER_KEYS.includes(layerKey)) {
                  return layersInfo;
                }

                return {
                  ...layersInfo,
                  [layerKey]: {
                    supportedFeatureCollectionIds: [
                      ...currSupportedFeatureCollectionIds,
                      fc?.get('id'),
                    ],
                    enrolledFeatureCollectionIds: isEnrolled
                      ? [...currEnrolledFeatureCollectionIds, fc?.get('id')]
                      : currEnrolledFeatureCollectionIds,
                    processingFeatureCollectionIds: isProcessing
                      ? [...currProcessingFeatureCollectionIds, fc?.get('id')]
                      : currProcessingFeatureCollectionIds,
                  },
                };
              },
              {}
            );
            return layersInfo;
          })
          .toList()
          .toJS()
      : createLayerKeysFromLayerObject(organization.get('supportedLayersBySource').toJS()).map(
          (layerKey) => ({
            [layerKey]: {
              supportedFeatureCollectionIds: [],
              enrolledFeatureCollectionIds: [],
              processingFeatureCollectionIds: [],
            },
          })
        );

    const consolidatedLayersMap: SupportedLayersMap = layersMap.reduce((acc, layerInfo) => {
      Object.entries(layerInfo).forEach(([layerKey, value]) => {
        let key = layerKey;
        const source = parseLayerKey(key).sourceId;

        // If we have a Planet Forest Carbon package layer, we don't want to display that directly
        // in the layersLibrary. Instead We want to display the package that represents them all. So
        // we replace the layerKey with the layerKey that represents the package. This will happen for
        // each of the forest carbon layer keys so we deduplicate the featureCollectionId arrays to avoid
        // repeats. NOTE: it is assumed that the enrolled/supported/processing featureCollectionIds will
        // be the same for each of these layers because they can only be manipulated as a group.
        if (`${source}_package` in LAYER_PACKAGES) {
          key = `${source}_package`;
        }
        if (key in acc) {
          acc[key].enrolledFeatureCollectionIds = Array.from(
            new Set([
              ...acc[key].enrolledFeatureCollectionIds,
              ...value.enrolledFeatureCollectionIds,
            ])
          );
          acc[key].supportedFeatureCollectionIds = Array.from(
            new Set([
              ...acc[key].supportedFeatureCollectionIds,
              ...value.supportedFeatureCollectionIds,
            ])
          );
          acc[key].processingFeatureCollectionIds = Array.from(
            new Set([
              ...acc[key].processingFeatureCollectionIds,
              ...value.processingFeatureCollectionIds,
            ])
          );
        } else {
          acc[key] = value;
        }
      });
      return acc;
    }, {});

    let layers: LibraryDataset<LayerDataset>[] = Object.entries(consolidatedLayersMap).map(
      ([
        layerKey,
        {
          supportedFeatureCollectionIds,
          enrolledFeatureCollectionIds,
          processingFeatureCollectionIds,
        },
      ]) => {
        const source = parseLayerKey(layerKey).sourceId;
        // Special override for Planet forest carbon package. Everywhere else we want the source to be called Planet Forest
        // Carbon but in the layers library it results in redundant information so we simplify to Planet Labs
        const sourceDisplayName =
          source === 'PLANET-FOREST-CARBON-30' || source == 'PLANET-FOREST-CARBON-3'
            ? 'Planet Labs'
            : CONSTANTS.SOURCE_DETAILS_BY_ID[source]
              ? `${CONSTANTS.SOURCE_DETAILS_BY_ID[source].operator} ${CONSTANTS.SOURCE_DETAILS_BY_ID[source].name}`.trim()
              : '';
        const premium = CONSTANTS.SOURCE_DETAILS_BY_ID[source]
          ? CONSTANTS.SOURCE_DETAILS_BY_ID[source].premium
          : false;
        const paid = !!CONSTANTS.SOURCE_DETAILS_BY_ID[source]?.paid;
        const billingIntervals = CONSTANTS.SOURCE_DETAILS_BY_ID[source]
          ? CONSTANTS.SOURCE_DETAILS_BY_ID[source].billing_intervals
          : [];
        const layer = getLayer(layerKey, true);
        const resolution = getLayerResolution(layerKey);
        const thumbnailUrl = layer.thumbnailUrl || DEFAULT_THUMBNAIL_URL;
        const tags = layer.tags || [];
        const libraryAction = layer.libraryAction;
        const restricted = layer.allowedTiers
          ? !orgIsAllowedTier(organization, layer.allowedTiers)
          : false;

        return {
          layerKey,
          libraryKey: layerKey,
          supportedFeatureCollectionIds,
          enrolledFeatureCollectionIds,
          processingFeatureCollectionIds,
          name: layer.shortName,
          source: sourceDisplayName,
          resolution: resolution,
          thumbnailUrl: thumbnailUrl,
          highResPreview: layer.highResPreview,
          premium,
          paid,
          description: layer.description || '',
          license: layer.license,
          tags,
          libraryAction,
          billingIntervals,
          restricted,
          type: LibraryDatasetType.LAYER,
        };
      }
    );

    layers = layers.filter((l) => {
      // Don't show individual layers that are a part of a package
      return !Object.values(LAYER_PACKAGES).flat().includes(l.layerKey);
    });

    // append any layers that we want to preview in the library to our list of returned layers.
    // returning these as proper Layer objects means we can display descriptions and other details.
    layers = [...layers, ...Object.values(PREVIEW_LAYERS_CONFIG)];

    // sort layers alphabetically by both name and source
    return layers
      .sort((a, b) => featureUtils.alphanumericSort(a.source, b.source))
      .sort((a, b) => featureUtils.alphanumericSort(a.name, b.name));
  }, [featureCollectionsById, projects, organization]);

  const addLayers = async (projectIds: string[], layerKey: string) => {
    recordEvent('Layer added from layers library', {layerKey: layerKey});
    const layerKeys = getPackageLayerKeys(layerKey);
    const layersObject = createLayerObjectFromLayerKeys(layerKeys);
    const featureCollectionIds = projectIds
      .map((pId) =>
        getPrimaryFeatureCollectionIdFromProjectId(pId, projects!, featureCollectionsById)
      )
      .filter((fcId) => fcId) as unknown as number[];

    // This section looks slightly different than in overlays. This is because our caching
    // logic is inside the projects provider, and cache restore behavior invokes a rejection.
    addLayersToFeatureCollections(featureCollectionIds, layersObject).catch((e) => {
      // e is unknown, add a type, use optional chaining, and provide a default
      const errorBody = (e as {body: {error: string}; error: Error})?.body?.error || '';

      Sentry.captureException(e);
      // In lieu of actual error types or enums, check to see if this error is about being readonly
      const errorMessage = errorBody.includes('readonly')
        ? 'Please reach out to an admin or member on your team to add layers.'
        : 'We weren’t able to add these layers. Please try again.';
      window.alert(errorMessage);
    });
  };

  const removeLayers = async (projectIds: string[], layerKey: string) => {
    const layerKeys = getPackageLayerKeys(layerKey);
    const layersObject = createLayerObjectFromLayerKeys(layerKeys);
    const fcIdsForLayerRemoval: number[] = projectIds
      .map((pId) =>
        getPrimaryFeatureCollectionIdFromProjectId(pId, projects!, featureCollectionsById)
      )
      .filter((fcId) => fcId) as unknown as number[];

    // check if there are any alert policies in the relevant fcs using the layers being removed
    if (alertPolicies && alertPolicies.length > 0) {
      const affectedAlertPolicies = alertPolicies.filter((policy) => {
        const policyFcId =
          typeof policy.featureCollectionId === 'string'
            ? parseInt(policy.featureCollectionId)
            : policy.featureCollectionId;
        if (!fcIdsForLayerRemoval.includes(policyFcId)) {
          return false;
        }

        return layerKeys.some((layerKey) => {
          const {sourceId, layerId} = parseLayerKey(layerKey);
          return policy.sourceId === sourceId && policy.layerId === layerId;
        });
      });

      if (affectedAlertPolicies.length > 0) {
        if (
          !confirm(
            `Are you sure you want to remove this layer? This will affect ${affectedAlertPolicies.length} lookout(s) using this layer.`
          )
        ) {
          return;
        }
      }
    }

    // This section looks slightly different than in overlays. This is because our caching
    // logic is inside the projects provider, and cache restore behavior invokes a rejection.
    removeLayersFromFeatureCollections(fcIdsForLayerRemoval, layersObject).catch((e) => {
      // e is unknown, add a type, use optional chaining, and provide a default
      const errorBody = (e as {body: {error: string}; error: Error})?.body?.error || '';

      Sentry.captureException(e);
      // In lieu of actual error types or enums, check to see if this error is about being readonly
      const errorMessage = errorBody.includes('readonly')
        ? 'Please reach out to an admin or member on your team to remove layers.'
        : "We weren't able to remove these layers. Please try again.";
      window.alert(errorMessage);
    });
  };

  // Batched add/remove for the bulk enrollment modal. Each feature collection gets
  // one process_sources job carrying all of its selected layers grouped by source
  // (the shape the raster pipeline processes most efficiently).
  const bulkUpdateLayers = async (mode: BulkMode, portfolioPlans: PortfolioPlan[]) => {
    if (!portfolioPlans.length) {
      return;
    }
    const layerCount = new Set(portfolioPlans.flatMap((p) => p.layerKeys)).size;
    recordEvent('Bulk updated layers', {
      mode,
      layerCount,
      portfolioCount: portfolioPlans.length,
    });

    const action =
      mode === 'add' ? addLayersToFeatureCollections : removeLayersFromFeatureCollections;

    const results = await Promise.allSettled(
      portfolioPlans.map((plan) => action([plan.featureCollectionId], plan.layersObject))
    );
    const failures = results.flatMap((result) =>
      result.status === 'rejected' ? [result.reason] : []
    );
    if (!failures.length) {
      return;
    }

    failures.forEach((failure) => Sentry.captureException(failure));
    const isReadonly = failures.some((failure) =>
      ((failure as {body?: {error?: string}})?.body?.error || '').includes('readonly')
    );
    // The successes stand, so name how much failed — a retry re-sends only those
    // portfolios, since the enrolled ones now compute as skips.
    const scope =
      portfolioPlans.length === 1
        ? 'these layers'
        : `these layers for ${failures.length} of ${portfolioPlans.length} portfolios`;
    window.alert(
      isReadonly
        ? 'Please reach out to an admin or member on your team to update layers.'
        : `We weren’t able to update ${scope}. Please try again.`
    );
    throw failures[0];
  };

  // Creates a map of featureCollectionIds to projectIds. We need to use projectIds
  // a lot throughout the UI to display portfolio names to add layers to and work more seamlessly
  // with the shared layers library code. Creating this map allows for an efficient lookup
  // when needing to translate between the two.
  const [projectIdToFcIdMap, fcIdToProjectMap] = React.useMemo(() => {
    const fcIdToProjectTuples = projects
      ? projects
          .valueSeq()
          .toList()
          .map((project) => {
            const primaryFeatureCollection = findPrimaryFeatureCollection(
              project!,
              featureCollectionsById
            );
            return [primaryFeatureCollection!.get('id'), project?.toJS()];
          })
          .toJS()
      : [];
    const projectIdToFcIdTuples = fcIdToProjectTuples.map(([fcId, project]) => [project.id, fcId]);
    return [
      Object.fromEntries(projectIdToFcIdTuples),
      Object.fromEntries(fcIdToProjectTuples) as Record<number, ApiProject>,
    ];
  }, [projects, featureCollectionsById]);

  const organizationId = organization.get('id');
  const [{value: subscribedPremiumSourceIds}, {refresh: refreshSubscribedPremiumSourceIds}] =
    useApiGet(
      async (organizationId: string) => {
        const subscribedPremiumSourceIds: string[] = (
          await api.organizations.listPremiumSources(organizationId)
        )
          .get('data')
          .toJS();
        return subscribedPremiumSourceIds;
      },
      [organizationId]
    );

  const isLoading = areProjectsLoading || subscribedPremiumSourceIds === undefined;

  const purchasePaidLayer = async (
    sourceId: string,
    billingInterval: string,
    subscriptionProrationDate: number,
    billingCycleAnchor: number,
    couponId: string | undefined
  ) => {
    try {
      return (
        await api.organizations.addPaidLayer(
          organizationId,
          sourceId,
          billingInterval,
          subscriptionProrationDate,
          billingCycleAnchor,
          couponId
        )
      ).getIn(['data', 'clientSecret']);
    } finally {
      // Alwaya refresh in case the addPaidLayer request hits the error
      // where someone else from the organization paid for this layer while this
      // user was paying for it
      await refreshSubscribedPremiumSourceIds();
    }
  };

  return (
    <ImageryPricesProvider>
      <LensLibraryView
        addLayers={addLayers}
        removeLayers={removeLayers}
        bulkUpdateLayers={bulkUpdateLayers}
        projectIdToFcId={projectIdToFcIdMap}
        alertPolicies={alertPolicies}
        alertPoliciesLoading={alertPoliciesLoading}
        alertPoliciesError={alertPoliciesError}
        selectedOverlayIds={selectedOverlayIds}
        setSelectedOverlayIds={setSelectedOverlayIds}
        layers={layers}
        projects={(projects || I.OrderedMap()).valueSeq().toList()}
        isLoading={isLoading}
        fcIdToProjectMap={fcIdToProjectMap}
        subscribedPremiumSourceIds={subscribedPremiumSourceIds || []}
        purchasePaidLayer={purchasePaidLayer}
        organization={organization}
        profile={profile}
        role={profile.get('role')}
        mode={mode}
        onClose={onClose}
      />
    </ImageryPricesProvider>
  );
};
